/**
 * Smart-Defaults: aus einem Datei-Namen ein paar Pre-Fills extrahieren.
 *
 * Bewusst dumm und konservativ — wenn ein Match nicht eindeutig ist, gib leeren
 * String zurueck. Der User korrigiert dann in der Row.
 */
import type { ExpenseCategory } from '@/shared/api';

interface ContractGuess {
  type: string;
  counterparty: string;
}

interface InvoiceGuess {
  supplier: string;
  category: ExpenseCategory | '';
  issuedOn: string;
}

const CONTRACT_TYPE_HINTS: Array<{ pattern: RegExp; type: string }> = [
  { pattern: /strom|eon|vattenfall|enbw/i, type: 'Strom' },
  { pattern: /gas/i, type: 'Gas' },
  { pattern: /wasser|stadtwerk/i, type: 'Wasser' },
  { pattern: /internet|wlan|vodafone|telekom|1und1|o2/i, type: 'Internet' },
  { pattern: /muell|abfall|entsorg/i, type: 'Müll' },
  { pattern: /versicher/i, type: 'Versicherung' },
  { pattern: /miete|nebenkosten/i, type: 'Miete' },
  { pattern: /wartung/i, type: 'Wartung' },
];

const COUNTERPARTY_HINTS: Array<{ pattern: RegExp; name: string }> = [
  { pattern: /vodafone/i, name: 'Vodafone' },
  { pattern: /telekom/i, name: 'Deutsche Telekom' },
  { pattern: /1und1|1&1/i, name: '1&1' },
  { pattern: /\bo2\b/i, name: 'o2' },
  { pattern: /eon|e\.on/i, name: 'E.ON' },
  { pattern: /vattenfall/i, name: 'Vattenfall' },
  { pattern: /enbw/i, name: 'EnBW' },
  { pattern: /allianz/i, name: 'Allianz' },
  { pattern: /huk/i, name: 'HUK' },
];

const INVOICE_CATEGORY_HINTS: Array<{ pattern: RegExp; category: ExpenseCategory }> = [
  { pattern: /strom|eon|vattenfall|enbw/i, category: 'Strom' },
  { pattern: /gas/i, category: 'Gas' },
  { pattern: /wasser/i, category: 'Wasser' },
  { pattern: /internet|vodafone|telekom|1und1|o2/i, category: 'Internet' },
  { pattern: /muell|abfall/i, category: 'Muell' },
  { pattern: /versicher/i, category: 'Versicherung' },
  { pattern: /reparatur|wartung/i, category: 'Reparatur' },
  { pattern: /reinigung|clean/i, category: 'Reinigung' },
  { pattern: /verwaltung|hausverwaltung/i, category: 'Verwaltung' },
  { pattern: /grundsteuer|kommunal/i, category: 'GrundsteuerKommunal' },
  { pattern: /zinsen|darlehen|kredit/i, category: 'Zinsen' },
];

export function guessContract(fileName: string): ContractGuess {
  const stem = stripExtension(fileName);
  const type = CONTRACT_TYPE_HINTS.find((h) => h.pattern.test(stem))?.type ?? '';
  const counterparty = COUNTERPARTY_HINTS.find((h) => h.pattern.test(stem))?.name ?? '';
  return { type, counterparty };
}

export function guessInvoice(fileName: string): InvoiceGuess {
  const stem = stripExtension(fileName);
  const supplier = COUNTERPARTY_HINTS.find((h) => h.pattern.test(stem))?.name ?? '';
  const category = INVOICE_CATEGORY_HINTS.find((h) => h.pattern.test(stem))?.category ?? '';
  const issuedOn = extractDate(stem) ?? '';
  return { supplier, category, issuedOn };
}

function stripExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

/** Extrahiert YYYY-MM-DD oder DD.MM.YYYY oder YYYY-MM aus dem Dateinamen. */
function extractDate(stem: string): string | null {
  const iso = stem.match(/(\d{4})[-_./](\d{2})[-_./](\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const de = stem.match(/(\d{2})[._-](\d{2})[._-](\d{4})/);
  if (de) return `${de[3]}-${de[2]}-${de[1]}`;
  const ym = stem.match(/(\d{4})[-_.](\d{2})/);
  if (ym) return `${ym[1]}-${ym[2]}-01`;
  return null;
}
